/*********************************************************************************************
* Creates a debounced function that delays invoking func until after duration milliseconds have elapsed since the last time the debounced function was invoked.
	*
	* @param {Function} func - The function to debounce.
	* @param {number} duration - The number of milliseconds to delay.
	* @returns {Function} - The debounced function.
*/
function debounce(func, duration) {
    let timeout;
    return function (...args) {
        const effect = () => {
            timeout = null;
            return func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(effect, duration);
    };
}

/*********************************************************************************************
* Get the full cart section ID from the DOM.
	*
	* @returns {HTMLElement} - The full cart section ID with the prefix.
*/
function mmCartSectionIdLong() {
	return document.querySelector('.mm-cart').id;
}

/*********************************************************************************************
* Get the cart section ID from the DOM, removing the "shopify-section-" prefix.
	*
	* @returns {HTMLElement} - The cart section ID without the "shopify-section-" prefix.
*/
function mmCartSectionId() {
	return document.querySelector('.mm-cart').id.replace('shopify-section-', '');
}

/*********************************************************************************************
* Get the cart section element from the DOM.
	*
	* @returns {HTMLElement} - The cart section element.
*/
function mmCartSectionElement() {
    return document.getElementById(`${mmCartSectionIdLong()}`);
}

/*********************************************************************************************
* Get the cart product container element from the cart section.
	*
	* @returns {HTMLElement} - The cart product container element.
*/
function mmCartProductsContainerElement() {
    return mmCartSectionElement().querySelector('.mm-drawer-cart-products-container');
}

/*********************************************************************************************
* Get the cart item element for a specific product variant.
	*
	* @param {number} line_index - The line item index.
	* @returns {HTMLElement} - The cart item element.
*/
function mmCartItem(line_index) {
    return mmCartProductsContainerElement().querySelector(`[data-line-index="${line_index}"]`);
}

/*********************************************************************************************
* Get the cart item quantity element for a specific product variant.
	*
	* @param {number} line_index - The line item index.
	* @returns {HTMLElement} - The cart item quantity element.
*/
function mmCartItemQuantityEl(line_index) {
    const itemEl = mmCartItem(line_index);
    return itemEl.querySelector('[data-item-quantity]');
}

/*********************************************************************************************
* Get the quantity of a specific cart item.
	*
	* @param {number} line_index - The line item index.
	* @returns {number} - The quantity of the cart item.
*/
function mmGetCartItemQuantity(line_index) {
    const quantityEl = mmCartItemQuantityEl(line_index);
    return Number.parseInt(quantityEl.dataset.itemQuantity);
}

/*********************************************************************************************
* Set the quantity of a specific cart item and update the DOM.
	*
	* @param {number} line_index - The line item index.
	* @param {number} newQuantity - The new quantity to set.
*/
function mmSetCartItemQuantity(line_index, newQuantity) {
    // Display quantity in front
    if (newQuantity >= 0) {
        const quantityEl = mmCartItemQuantityEl(line_index);
        quantityEl.dataset.itemQuantity = newQuantity;
        // quantityEl.innerText = newQuantity;
    }
}

/*********************************************************************************************
* Create a new cart element from the provided data.
	*
	* @param {Object} data - The data containing cart HTML.
	* @returns {HTMLElement} - The new cart element.
*/
function mmCartHTMLFromData(data) {
    const el = document.createElement('DIV');
    el.innerHTML = data.sections['mm-cart'];
    return el;
}

/*********************************************************************************************
* Extract the items container from the new cart HTML.
	*
	* @param {Object} data - The data containing cart HTML.
	* @returns {HTMLElement} - The items container element.
*/
function extractItemsFromCartHTML(data) {
    const el = mmCartHTMLFromData(data);
    return el.querySelector('.mm-drawer-cart-products-container');
}

/*********************************************************************************************
* Insert cart products from the response data into the current cart in the DOM.
	*
	* @param {Object} data - The data containing the new cart HTML.
	* @returns {Object} - The response data.
*/
function insertCartProductsFromResponse(data) {
    const itemsContainer = extractItemsFromCartHTML(data);
    mmCartProductsContainerElement().innerHTML = itemsContainer.innerHTML;
    return data;
}

/*********************************************************************************************
* Extract the total price element from the new cart HTML.
	*
	* @param {Object} data - The data containing the new cart HTML.
	* @returns {HTMLElement} - The total price element.
*/
function extractTotalPriceFromCartHTML(data) {
    const cartEl = mmCartHTMLFromData(data);
    return cartEl.querySelector('[data-cart-total-price]');
}

/*********************************************************************************************
* Insert the total price from the response data into the current cart in the DOM.
	*
	* @param {Object} data - The data containing the new cart HTML.
	* @returns {Object} - The response data.
*/
function insertInsertTotalPriceFromResponse(data) {
    const newTotalPriceEl = extractTotalPriceFromCartHTML(data);
    const currentTotalPriceEl = mmCartSectionElement().querySelector('[data-cart-total-price]');
    currentTotalPriceEl.innerHTML = newTotalPriceEl.innerHTML;
    return data;
}

/*********************************************************************************************
* Perform an API request to fetch or update cart data.
	*
	* @param {string} method - The HTTP method (GET, POST, etc.).
	* @param {string} path - The API endpoint path.
	* @param {Object} body - The request body data.
	* @returns {Promise<Object>} - A promise resolving to the response data.
*/
function mmAPIRequest(method, path, body) {
    return fetch(window.Shopify.routes.root + path, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    })
        .then((response) => response.json())
        .then((data) => {
            // mmLog(data);
            updateCartCount();
            if (data.status && data.status > 400) {
                throw data;
            } else {
                return data;
            }
        });
}

/*********************************************************************************************
* Fetch the current cart data.
	*
	* @returns {Promise<Object>} - A promise resolving to the cart data.
*/
function mmGetCart() {
    return mmAPIRequest('GET', 'cart.js');
}

/*********************************************************************************************
	* Open the cart drawer and fetch the latest cart data.
*/
function mmOpenCart() {
    mmGetCart();
    //mmSetupProductForm();
	const cart = document.querySelector('.mm-drawer-cart-container');
    const html = document.querySelector('html');

    if (!cart || !html ) return;

    cart.classList.add('mm-drawer-cart-is-open');

    setTimeout(() => {
        html.classList.toggle('stop-scrolling');
    }, 100);
}

/*********************************************************************************************
* Close the cart drawer and allow page scrolling.
	*
*/
function mmCloseCart() {
    document.querySelector('.mm-drawer-cart-container')?.classList.remove('mm-drawer-cart-is-open');
    document.querySelector('html').classList.remove('stop-scrolling');
}


/*********************************************************************************************
* Re-render the cart inner HTML from the response data and initialize Splide carousels.
	*
	* @param {Object} data - The data containing the new cart HTML.
	* @returns {Object} - The response data.
*/
function refreshCartFromData(data) {
	const newCartEl = document.createElement('DIV');
	newCartEl.innerHTML = data.sections[mmCartSectionId()];
	const innerCart = mmCartSectionElement().querySelector('.mm-drawer-cart-container');
	innerCart.innerHTML = newCartEl.querySelector('.mm-drawer-cart-container').innerHTML;

	mmSetupCrossSellProductForms();
	mmSetupFreeProductForms();
	
	// Vérifier si le produit gratuit était ajouté mais n'est plus dans le panier
	if (sessionStorage.getItem('mm_free_product_added') === 'true' && sessionStorage.getItem('mm_free_product_manually_removed') !== 'true') {
		const freeProductForms = document.querySelectorAll(".mm-cart-free-product-item [data-type='add-to-cart-form']");
		if (freeProductForms.length > 0) {
			// Re-ajouter automatiquement le produit gratuit
			const freeProductForm = freeProductForms[0];
			const formData = new FormData(freeProductForm);
			
			fetch("/cart/add.js", {
				method: "POST",
				body: formData,
			})
			.then(response => response.json())
			.then(data => {
				// Rafraîchir le panier pour afficher le changement
				setTimeout(() => {
					mmOpenAndRefreshCart(data);
				}, 500);
			})
			.catch(error => {
				console.error("Erreur lors du re-ajout automatique:", error);
				// En cas d'erreur, nettoyer le sessionStorage
				sessionStorage.removeItem('mm_free_product_added');
			});
		}
	}
	
	initializeSectionsWithSplide('data-section-cart', '.mm-drawer-cart-reassurance-splide', initializeSplideReassuranceCart);
	mmSetupDynamicPaymentButtons();
	return data;
}

/*********************************************************************************************
	* Sets up the cart form submission handler.
*/
function mmSetupProductForm() {
	const forms = document.querySelectorAll("[data-type='add-to-cart-form']");
	if (!forms) return;

	forms.forEach(form => {
		form.addEventListener("submit", function (event) {
			event.preventDefault();

			const formData = new FormData(form);

			fetch("/cart/add.js", {
				method: "POST",
				body: formData,
			})
			.then(response => response.json())
			.then(data => {
				mmOpenAndRefreshCart(data);
			})
			.catch(error => {
				console.error("Erreur:", error);
			});
		});
	});
}
function mmSetupCrossSellProductForms() {
	const forms = document.querySelectorAll(".mm-drawer-cart-cross-sell-container-item [data-type='add-to-cart-form']");
	if (!forms) return;

	forms.forEach(form => {
		// Supprimer l'ancien event listener s'il existe
		if (form.mmCrossSellHandler) {
			form.removeEventListener("submit", form.mmCrossSellHandler);
		}
		
		// Créer le nouveau handler
		form.mmCrossSellHandler = function (event) {
			event.preventDefault();

			const formData = new FormData(form);

			fetch("/cart/add.js", {
				method: "POST",
				body: formData,
			})
			.then(response => response.json())
			.then(data => {
				mmOpenAndRefreshCart(data);
			})
			.catch(error => {
				console.error("Erreur:", error);
			});
		};
		
		// Ajouter le nouveau event listener
		form.addEventListener("submit", form.mmCrossSellHandler);
	});
}

/*********************************************************************************************
* Sets up the free product form submission handler.
*/
function mmSetupFreeProductForms() {
	const forms = document.querySelectorAll(".mm-cart-free-product-item [data-type='add-to-cart-form']");
	if (!forms) return;

	forms.forEach(form => {
		// Supprimer l'ancien event listener s'il existe
		if (form.mmFreeProductHandler) {
			form.removeEventListener("submit", form.mmFreeProductHandler);
		}
		
		// Créer le nouveau handler
		form.mmFreeProductHandler = function (event) {
			event.preventDefault();

			const formData = new FormData(form);
			
			// Récupérer l'ID du produit gratuit depuis le formulaire
			const variantId = formData.get('id');
			
			// Marquer que l'utilisateur a ajouté un produit gratuit
			sessionStorage.setItem('mm_free_product_added', 'true');
			// Stocker l'ID du produit gratuit
			sessionStorage.setItem('mm_free_product_variant_id', variantId);
			// Réinitialiser le marqueur de suppression manuelle
			sessionStorage.removeItem('mm_free_product_manually_removed');

			fetch("/cart/add.js", {
				method: "POST",
				body: formData,
			})
			.then(response => response.json())
			.then(data => {
				mmOpenAndRefreshCart(data);
			})
			.catch(error => {
				console.error("Erreur:", error);
				// Retirer le marqueur en cas d'erreur
				sessionStorage.removeItem('mm_free_product_added');
				sessionStorage.removeItem('mm_free_product_variant_id');
			});
		};
		
		// Ajouter le nouveau event listener
		form.addEventListener("submit", form.mmFreeProductHandler);
	});
}

/*********************************************************************************************
* Refresh the cart and open the cart drawer
*/
function mmOpenAndRefreshCart(data) {
    function handleResponse() {
        mmOpenCart();
        const newCartEl = document.createElement("DIV");
        newCartEl.innerHTML = this.responseText;
        const innerCart = mmCartSectionElement().querySelector(".mm-drawer-cart-container");

        // Si le cross-sell est visible, on le garde visible dans le nouveau DOM
        const crossSell = document.querySelector('.mm-drawer-cart-cross-sell');
        if (crossSell?.classList.contains('is-visible-mobile')) {
            const newCrossSell = newCartEl.querySelector('.mm-drawer-cart-cross-sell');
            if (newCrossSell) newCrossSell.classList.add('is-visible-mobile');
        }

        // Remplacer l'ancien contenu par le nouveau
        innerCart.innerHTML = newCartEl.querySelector(".mm-drawer-cart-container").innerHTML;

        // 🎯 Maintenant que le nouveau contenu est dans le DOM, on peut changer le texte du bouton
        const checkoutButton = innerCart.querySelector('.mm-drawer-cart-info-cta-checkout-cross-sell span');
        if (checkoutButton) checkoutButton.innerText = "Continue to payment";

        const reassuranceContainer = innerCart.querySelector('.mm-drawer-cart-cta-reassurance');
        if (reassuranceContainer) {
            reassuranceContainer.classList.add('mm-drawer-cart-cta-reassurance-updated');
        }

        mmGetCart();
        const line_index = document.querySelector(`.mm-cart-item[data-cart-item-id="${data.variant_id}"]`)?.dataset.lineIndex;

        // Maintenant qu'on a notre nouveau HTML, il faut remettre des event listener sur les formulaire
        mmSetupCrossSellProductForms();
        mmSetupFreeProductForms();
        
        // Vérifier si le produit gratuit était ajouté mais n'est plus dans le panier
        if (sessionStorage.getItem('mm_free_product_added') === 'true' && sessionStorage.getItem('mm_free_product_manually_removed') !== 'true') {
            const freeProductForms = document.querySelectorAll(".mm-cart-free-product-item [data-type='add-to-cart-form']");
            if (freeProductForms.length > 0) {
                // Re-ajouter automatiquement le produit gratuit
                const freeProductForm = freeProductForms[0];
                const formData = new FormData(freeProductForm);
                
                fetch("/cart/add.js", {
                    method: "POST",
                    body: formData,
                })
                .then(response => response.json())
                .then(data => {
                    // Rafraîchir le panier pour afficher le changement
                    setTimeout(() => {
                        mmOpenAndRefreshCart(data);
                    }, 500);
                })
                .catch(error => {
                    console.error("Erreur lors du re-ajout automatique:", error);
                    // En cas d'erreur, nettoyer le sessionStorage
                    sessionStorage.removeItem('mm_free_product_added');
                });
            }
        }
        
        initializeSectionsWithSplide('data-section-cart', '.mm-drawer-cart-reassurance-splide', initializeSplideReassuranceCart);
        mmSetupDynamicPaymentButtons();
    }

    const request = new XMLHttpRequest();
    request.addEventListener("load", handleResponse);
    request.open("GET", `/?section_id=${mmCartSectionId()}`, true);
    request.send();
}

/*********************************************************************************************
* Increase the quantity of a specific product in the cart by one.
	*
	* @param {number} line_index - The line item index.
*/
function mmIncreaseProductQuantity(line_index, button) {
    const itemCart = mmCartItem(line_index);
//   console.log('itemCart', itemCart)
    const quantity = mmGetCartItemQuantity(line_index);
    const maxQtty = parseInt(itemCart.dataset.maxQuantity, 10);
    const newQuantity = quantity + 1;
//   console.log('line_index', line_index)
//   console.log('newQuantity', newQuantity)
//   console.log('maxQtty', maxQtty)

    if (newQuantity > maxQtty) {
        return;
    }
//   console.log('line_index', line_index)

    mmSetCartItemQuantity(line_index, newQuantity);
    mmDebouncedPushNewQuantity(line_index);
}

/*********************************************************************************************
	* Decrease the quantity of a specific product in the cart by one.
	*
	* @param {number} line_index - The line item index.
*/
function mmDecreaseProductQuantity(line_index, button) {
    const itemCart = mmCartItem(line_index);
    const quantity = mmGetCartItemQuantity(line_index);
    const maxQtty = parseInt(itemCart.dataset.maxQuantity, 10);
    const newQuantity = quantity - 1;

    if (newQuantity > maxQtty) {
        return;
    }

    mmSetCartItemQuantity(line_index, newQuantity);
    mmDebouncedPushNewQuantity(line_index);
}

/*********************************************************************************************
* Remove a specific product from the cart.
	*
	* @param {number} line_index - The line item index.
*/
function mmDestroyProductFromCart(line_index) {
    // Vérifier si le produit supprimé est un produit gratuit
    const productVariantId = mmGetProductVariantId(line_index);
    const storedFreeProductId = sessionStorage.getItem('mm_free_product_variant_id');
    
    // Vérifier si l'ID du produit supprimé correspond à l'ID du produit gratuit stocké
    if (sessionStorage.getItem('mm_free_product_added') === 'true' && 
        storedFreeProductId && 
        productVariantId === storedFreeProductId) {
        // Si on supprime le produit gratuit, marquer qu'il a été supprimé volontairement
        sessionStorage.setItem('mm_free_product_manually_removed', 'true');
        sessionStorage.removeItem('mm_free_product_added');
        sessionStorage.removeItem('mm_free_product_variant_id');
    }
    
    const newQuantity = 0;
    mmSetCartItemQuantity(line_index, newQuantity);
    mmDebouncedPushNewQuantity(line_index);
}

/*********************************************************************************************
* Variant id
*/
function mmGetProductVariantId(line_index) {
    const itemEl = mmCartItem(line_index);
    return itemEl?.dataset?.cartItemId || null;
}

/*********************************************************************************************
* Push the new quantity of a specific product to the server.
	*
	* @param {number} line_index - The line item index.
*/
function mmPushNewQuantity(line_index) {
    const quantity = mmGetCartItemQuantity(line_index);
    const productVariantId = mmGetProductVariantId(line_index);

//   console.log('inside function')

    mmAPIRequest('POST', 'cart/change.js', {
        id: productVariantId.toString(),
        line: line_index,
        quantity: quantity,
        sections: mmCartSectionId(),
    })
    .then(refreshCartFromData)
    .then((data) => {
    })
    .catch((error) => {
        alert(error.message)
        console.log(error);     
        location.reload();
    });
}

/*********************************************************************************************
* Remove a line item from the cart.
	*
	* @param {number|string} line_index - The index of the line item in the cart to be removed.
*/
function mmDestroyLineItemFromCart(line_index) {
    mmAPIRequest("POST", "cart/change.js", {
        line: parseInt(line_index),
        quantity: 0,
        sections: mmCartSectionId(),
    })
    .then(refreshCartFromData);
}

/*********************************************************************************************
* Debounced function to push new quantity of a specific product to the server.
	*
	* @param {number} line_index - The line item index.
 */
const mmDebouncedPushNewQuantity = debounce(function (line_index, maxQtty) {
	const quantity = mmGetCartItemQuantity(line_index);

    if (quantity > maxQtty) {
        return;
    }
	mmPushNewQuantity(line_index);
}, 300);

/*********************************************************************************************
* Update the cart count bubble.
*/
function updateCartCount() {
    // console.log("updateCartCount appelée !"); 
    function handleResponse() {
        const tempDiv = document.createElement("DIV");
        tempDiv.innerHTML = this.responseText;

        // On cible le conteneur du panier dans le header
        const headerSection = document.querySelector('[data-section-header]');
        if (!headerSection) return;
        
        const headerSectionId = headerSection.dataset.sectionHeader;
        
        // Mise à jour du compteur dans l'en-tête
        const newCartCount = tempDiv.querySelector('.mm-cart-count-bubble');
        const currentCartCount = document.querySelector('.mm-header-cart-container .mm-cart-count-bubble');
        
        if (newCartCount && currentCartCount) {
            currentCartCount.outerHTML = newCartCount.outerHTML;
        } else if (newCartCount && !currentCartCount) {
            document.querySelector('.mm-header-cart-container').appendChild(newCartCount.cloneNode(true));
        } else if (!newCartCount && currentCartCount) {
            currentCartCount.remove();
        }
    }

    // On récupère l'ID de la section header
    const headerSection = document.querySelector('[data-section-header]');
    if (!headerSection) return;
    
    const headerSectionId = headerSection.dataset.sectionHeader;

    const request = new XMLHttpRequest();
    request.addEventListener("load", handleResponse);
    request.open("GET", `/?section_id=${headerSectionId}`, true);
    request.send();
}

/*********************************************************************************************
* Add or remove free products from the cart based on conditions.
	*
	* @param {Object} data - The data containing the new cart HTML.
	* @returns {Object} - The response data.
*/
function mmAddOrRemoveFreeProducts(data) {
    if (data.sections['mm-cart']) {
        const el = mmCartHTMLFromData(data);
        const freeProductEls = el.querySelector('[data-free-products]');
        
        // Si l'élément data-free-products n'existe pas, on ne fait rien
        if (!freeProductEls) {
            console.log('No free products container found, skipping free product management');
            return data;
        }
        
        let freeProductCounts = 0;

        // creates an object like { 44058675: 1, 44058001: 0 }
        // where key is free product id and value, its quantity
        const productsParams = Array.from(freeProductEls.children).reduce((param, freeProductEl) => {
            const productId = freeProductEl.dataset.variantId;
            const isFree = freeProductEl.dataset.freeProduct == 'true';
            const itemEl = mmCartItem(productId);
            if (isFree) {
                freeProductCounts += 1;
            }
            if (itemEl && isFree) {
                console.log('do nothing, item is already added');
                // do nothing, item is already added
            } else if (itemEl && !isFree) {
                console.log('item is present but should be removed');
                // item is present but should be removed
                param[productId] = 0;
            } else if (!itemEl && isFree) {
                console.log('item is absent but should be added', productId);
                // item is absent but should be added
                param[productId] = 1;
            }
            return param;
        }, {});

        // Add or remove free products only if needed
        if (Object.keys(productsParams).length > 0) {
            // console.log('productsParams', productsParams);
            mmAPIRequest('POST', 'cart/update.js', { updates: productsParams, sections: mmCartSectionId() }).then(refreshCartFromData);
        }
    }
    return data;
}

/*********************************************************************************************
* Add a free product to the cart and optionally open the cart drawer.
	*
	* @param {number} productId - The free product ID to add.
	* @param {Object} options - Options for adding the product.
	* @param {number} [options.quantity=1] - The quantity to add.
	* @param {boolean} [options.openCart=true] - Whether to open the cart drawer after adding the product.
	* @param {HTMLElement} clickedElem - The element that was clicked to trigger this action.
*/
function mmAddFreeProductToCart(productId, { quantity = 1, openCart = true }, clickedElem) {
    // Debounce on the CTA
    clickedElem.innerText = 'AJOUTÉ !';
    clickedElem.classList.add('mm-disabled');

    // Ajouter le free product
    mmAddProductToCart(productId, { openCart: false });
}

/*********************************************************************************************
 * Splide reassurance - Initialisation
 *
 */
function initializeSplideReassuranceCart(splideElem) {
    var splideInstance = new Splide(splideElem, {
        type        : 'loop',
        arrows      : false,
        pagination  : true,
        perPage     : 1,
        drag        : 'free',
        snap        : true,
        fixedWidth  : '100%',
        autoplay    : true,
        interval    : 4000, 
        pauseOnHover: false
    }).mount();

    // Ajout de l'écouteur d'événement pour le défilement horizontal
    addHorizontalScrollListener(splideElem, splideInstance);
}

/*********************************************************************************************
* Masquer cross sell
	*
*/
function showFilterOverlayMobile() {
    const filter = document.querySelector('.mm-drawer-cart-cross-sell');
    if (!filter) return;

    filter.classList.add('is-visible-mobile');
}
function masqueFilterOverlayMobile() {
    const filter = document.querySelector('.mm-drawer-cart-cross-sell');
    if (!filter) return;

    filter.classList.remove('is-visible-mobile');
}

/*********************************************************************************************
* Checks if the browser is Safari (but not Chrome or other WebKit browsers)
*/
function isSafari() {
    return /^((?!chrome|android).)*safari/.test(navigator.userAgent.toLowerCase());
}

/*********************************************************************************************
* Setup the dynamic payment button to show either ApplePay or PayPal
*/
function mmSetupDynamicPaymentButtons() {
    const buttons = document.querySelectorAll('[data-mm-payment-button]');
    if (!buttons.length) return;

    const iconType = isSafari() ? 'applepay' : 'paypal';

    buttons.forEach(button => {
        const iconToShow = button.querySelector(`.mm-icon-${iconType}`);
        const iconToHide = button.querySelector(`.mm-icon-${iconType === 'applepay' ? 'paypal' : 'applepay'}`);
        
        if (iconToShow) {
            iconToShow.classList.remove('mm-none');
        }
        if (iconToHide && !iconToHide.classList.contains('mm-none')) {
            iconToHide.classList.add('mm-none');
        }
    });
}

/*********************************************************************************************
* Loaded Listenner
	*
*/
window.addEventListener('DOMContentLoaded', (event) => {
    //mmSetupProductForm();
    mmGetCart();
    // mmOpenCart();
    initializeSectionsWithSplide('data-section-cart', '.mm-drawer-cart-reassurance-splide', initializeSplideReassuranceCart);
    mmSetupDynamicPaymentButtons();
});

document.addEventListener('DOMContentLoaded', () => {
	mmSetupProductForm();
	mmSetupFreeProductForms();
},
{ once: true });